/**
 * VAP Voice Session - Gemini Implementation
 *
 * Implements VoiceSessionProvider for Gemini Multimodal Live API.
 * Uses audio primitives for capture/playback, handles Gemini-specific protocol.
 *
 * BOUNDARY: See /VOICE_BOUNDARY.md for ownership rules.
 * - All Gemini quirks encapsulated here
 * - UI sees only provider-agnostic types
 *
 * @module gemini/vap-session
 */

import { AudioCapture, type AudioCaptureCallbacks } from './audio-capture';
import { AudioPlayback } from './audio-playback';
import {
  type VoiceSessionProvider,
  type VoiceSessionConfig,
  type VoiceSessionStatus,
  type TranscriptTurn,
  type SessionPhase,
  type PhaseState,
} from './provider';

// ============================================================================
// Re-export provider types as VAP types (for backwards compatibility)
// ============================================================================

/** @deprecated Use TranscriptTurn from provider.ts */
export type VapTranscriptTurn = TranscriptTurn;

/** @deprecated Use VoiceSessionStatus from provider.ts */
export type VapSessionStatus = VoiceSessionStatus;

/** Minimum warmup duration in ms when enabled (prevents very short durations) */
const MIN_WARMUP_DURATION_MS = 10000; // 10 seconds

/**
 * VAP session configuration
 *
 * Extends VoiceSessionConfig with Gemini-specific options.
 */
export interface VapSessionConfig extends VoiceSessionConfig {
  // VoiceSessionConfig provides: systemPrompt, token, voiceName, callbacks
  // Gemini-specific additions can go here if needed
}

// ============================================================================
// Gemini WebSocket Protocol Types
// ============================================================================

interface GeminiSetupMessage {
  setup: {
    model?: string; // Optional: proxy specifies model for security
    generationConfig: {
      responseModalities: string[];
      speechConfig?: {
        voiceConfig: {
          prebuiltVoiceConfig: {
            voiceName: string;
          };
        };
      };
    };
    systemInstruction?: {
      parts: Array<{ text: string }>;
    };
    realtimeInputConfig?: {
      automaticActivityDetection?: {
        disabled?: boolean;
      };
    };
    inputAudioTranscription?: Record<string, never>;
    outputAudioTranscription?: Record<string, never>;
  };
}

interface GeminiRealtimeInput {
  realtimeInput: {
    mediaChunks: Array<{
      mimeType: string;
      data: string; // Base64 PCM
    }>;
  };
}

interface GeminiServerContent {
  serverContent?: {
    modelTurn?: {
      parts?: Array<{
        text?: string;
        inlineData?: {
          mimeType: string;
          data: string;
        };
      }>;
    };
    turnComplete?: boolean;
    interrupted?: boolean;
    inputTranscription?: {
      text: string;
    };
    outputTranscription?: {
      text: string;
    };
  };
  setupComplete?: Record<string, never>;
}

// ============================================================================
// VAP Session Implementation
// ============================================================================

/**
 * VAP Voice Session - Gemini Implementation
 *
 * Implements VoiceSessionProvider using Gemini Multimodal Live API.
 * Manages a voice assessment conversation and returns transcript.
 */
export class VapSession implements VoiceSessionProvider {
  private config: VapSessionConfig;
  private status: VapSessionStatus = 'idle';
  private ws: WebSocket | null = null;
  private audioCapture: AudioCapture | null = null;
  private audioPlayback: AudioPlayback | null = null;

  /** Flag to prevent onclose from emitting errors during user-initiated disconnect */
  private isClosing = false;

  // Transcript accumulation
  private transcript: VapTranscriptTurn[] = [];
  private pendingUserText = '';
  private pendingAgentText = '';

  // Parse error tracking - consecutive failures may indicate protocol issues
  private consecutiveParseErrors = 0;
  private readonly MAX_CONSECUTIVE_PARSE_ERRORS = 5;

  // =========================================================================
  // Phase State (F-004)
  // =========================================================================

  /** Current session phase (null if phase control disabled) */
  private currentPhase: SessionPhase | null = null;

  /** When current phase started (ISO 8601) */
  private phaseStartedAt: string | null = null;

  /** Timer for auto-transition from warm-up to in-character */
  private warmupTimer: ReturnType<typeof setTimeout> | null = null;

  /** Effective warmup duration (clamped to minimum) */
  private effectiveWarmupDurationMs: number = 0;

  // NOTE: Model selection is handled by the voice proxy server for security.
  // The proxy uses: gemini-live-2.5-flash-native-audio (Vertex AI Live API)

  constructor(config: VapSessionConfig) {
    this.config = config;

    // Initialize phase config if provided
    if (config.phaseConfig) {
      const requestedDuration = config.phaseConfig.warmupDurationMs;
      // 0 = disabled (no auto-transition, stays in warm_up until manual end)
      // >0 but <MIN = clamp to MIN (prevent very short durations)
      // >=MIN = use as-is
      if (requestedDuration === 0) {
        this.effectiveWarmupDurationMs = 0; // Disabled
      } else {
        this.effectiveWarmupDurationMs = Math.max(requestedDuration, MIN_WARMUP_DURATION_MS);
      }
    }
  }

  /**
   * Get current session status
   */
  getStatus(): VapSessionStatus {
    return this.status;
  }

  /**
   * Get current transcript
   */
  getTranscript(): VapTranscriptTurn[] {
    return [...this.transcript];
  }

  /**
   * Get current phase state (F-004).
   * Returns null if phase control is not enabled.
   */
  getPhaseState(): PhaseState | null {
    if (!this.config.phaseConfig || !this.currentPhase || !this.phaseStartedAt) {
      return null;
    }

    return {
      phase: this.currentPhase,
      phaseStartedAt: this.phaseStartedAt,
      warmupDurationMs: this.effectiveWarmupDurationMs,
    };
  }

  /**
   * Connect and start the voice session
   *
   * Connects through the voice proxy server which handles Vertex AI authentication.
   * The proxy is at /api/v1/voice/ws and requires a learner token.
   */
  async connect(): Promise<void> {
    if (this.status !== 'idle') {
      throw new Error('Session already started');
    }

    this.setStatus('connecting');

    try {
      // Connect WebSocket
      await this.connectWebSocket();

      // Guard: bail if disconnect() was called during connection
      if (this.isClosing) {
        return;
      }

      // Send setup message
      this.sendSetup();

      // Wait for setup complete
      await this.waitForSetupComplete();

      // Guard: bail if disconnect() was called during setup
      if (this.isClosing) {
        return;
      }

      this.setStatus('connected');

      // Initialize audio
      await this.initializeAudio();

      // Guard: bail if disconnect() was called during audio init
      if (this.isClosing) {
        return;
      }

      this.setStatus('active');

      // Start phase control if enabled (F-004)
      if (this.config.phaseConfig) {
        this.startWarmupPhase();
      }
    } catch (error) {
      // Cleanup any partially initialized resources
      this.cleanup();
      this.setStatus('error');
      const err = error instanceof Error ? error : new Error(String(error));
      this.config.onError?.(err);
      throw err;
    }
  }

  // ==========================================================================
  // Private: Phase Management (F-004)
  // ==========================================================================

  /**
   * Start the warm-up phase.
   * Sets initial phase state and starts the timer for auto-transition.
   */
  private startWarmupPhase(): void {
    this.currentPhase = 'warm_up';
    this.phaseStartedAt = new Date().toISOString();

    // Notify callback
    this.emitPhaseChange();

    // Start timer for auto-transition
    if (this.effectiveWarmupDurationMs > 0) {
      this.warmupTimer = setTimeout(() => {
        this.transitionToInCharacter();
      }, this.effectiveWarmupDurationMs);
    }
  }

  /**
   * Transition from warm-up to in-character phase.
   *
   * NOTE: Since Gemini protocol only sets systemInstruction at setup,
   * we cannot change the prompt mid-session. Instead, the initial prompt
   * contains instructions for both phases, and we rely on:
   * 1. Timer-based UI indication of phase change
   * 2. The agent's internal awareness (from initial prompt) to adjust behavior
   *
   * The "transition" phase is brief and internal (not exposed to UI).
   */
  private transitionToInCharacter(): void {
    // Clear timer if called manually
    if (this.warmupTimer) {
      clearTimeout(this.warmupTimer);
      this.warmupTimer = null;
    }

    // Skip if already in-character or not in warm-up
    if (this.currentPhase !== 'warm_up') {
      return;
    }

    // Brief transition phase (internal only, not exposed to UI)
    this.currentPhase = 'transition';

    // Move to in-character immediately
    // (No "wait for acknowledgment" - per risk flag, this has no reliable signal)
    this.currentPhase = 'in_character';
    this.phaseStartedAt = new Date().toISOString();

    // Notify callback
    this.emitPhaseChange();
  }

  /**
   * Emit phase change callback with current state.
   */
  private emitPhaseChange(): void {
    const state = this.getPhaseState();
    if (state) {
      this.config.onPhaseChange?.(state);
    }
  }

  /**
   * Clear phase timer (called during cleanup).
   */
  private clearPhaseTimer(): void {
    if (this.warmupTimer) {
      clearTimeout(this.warmupTimer);
      this.warmupTimer = null;
    }
  }

  /**
   * Cleanup all resources (called on error or disconnect)
   */
  private cleanup(): void {
    this.isClosing = true;

    // Clear phase timer
    this.clearPhaseTimer();

    // Stop audio resources
    this.stopAudio();

    // Close WebSocket
    if (this.ws) {
      try {
        if (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING) {
          this.ws.close(1000, 'Cleanup');
        }
      } catch {
        // Ignore close errors during cleanup
      }
      this.ws = null;
    }

    // Clear pending resolver
    this.setupCompleteResolver = null;
  }

  /**
   * Disconnect and return final transcript
   *
   * Per VOICE_BOUNDARY.md: Returns transcript for artifact creation.
   * Caller is responsible for creating the artifact.
   *
   * Safe to call at any point - cancels in-flight connect() if needed.
   */
  async disconnect(): Promise<VapTranscriptTurn[]> {
    // Flush any pending transcript before cleanup
    this.flushPendingTranscript();

    // Cleanup handles: setting isClosing, closing sockets (including CONNECTING), stopping audio
    this.cleanup();

    // Set status to ended (cleanup doesn't set status)
    this.setStatus('ended');

    return this.getTranscript();
  }

  /**
   * Mute/unmute microphone
   */
  setMuted(muted: boolean): void {
    this.audioCapture?.setMuted(muted);
  }

  // ==========================================================================
  // Private: WebSocket Management
  // ==========================================================================

  private async connectWebSocket(): Promise<void> {
    // Connect to the voice proxy server (handles Vertex AI auth)
    // Proxy URL: wss://host/api/v1/voice/ws
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    // In dev, Vite proxy doesn't reliably forward WebSocket - use direct backend URL
    const wsHost = import.meta.env.VITE_VOICE_WS_HOST || window.location.host;
    const wsUrl = `${protocol}//${wsHost}/api/v1/voice/ws`;

    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error('WebSocket connection timeout'));
      }, 10000);

      // Token passed via Sec-WebSocket-Protocol header for security
      // Format: access_token.{jwt} - keeps token out of URL logs
      this.ws = new WebSocket(wsUrl, [`access_token.${this.config.token}`]);

      this.ws.onopen = () => {
        clearTimeout(timeout);
        resolve();
      };

      this.ws.onerror = () => {
        clearTimeout(timeout);
        reject(new Error('WebSocket connection failed'));
      };

      this.ws.onclose = (event) => {
        // Don't emit errors for user-initiated disconnect or if already in error/ended state
        if (this.isClosing || this.status === 'ended' || this.status === 'error') {
          return;
        }
        this.setStatus('error');
        // Provide meaningful error messages based on close codes
        let errorMessage = `Connection closed: ${event.code}`;
        if (event.reason) {
          errorMessage += ` - ${event.reason}`;
        }
        // Map proxy close codes to user-friendly messages
        switch (event.code) {
          case 4001:
            errorMessage = 'Authentication failed. Please generate a new learner link.';
            break;
          case 4003:
            errorMessage = 'Voice service temporarily unavailable. Please try again.';
            break;
          case 4004:
            errorMessage = 'Session timed out due to inactivity.';
            break;
          case 4005:
            errorMessage = 'Maximum session duration reached.';
            break;
        }
        this.config.onError?.(new Error(errorMessage));
      };

      this.ws.onmessage = async (event) => {
        // Handle both string and Blob messages
        // Vertex AI sends JSON as text, but WebSocket may receive as Blob
        let data: string;
        if (event.data instanceof Blob) {
          data = await event.data.text();
        } else {
          data = event.data;
        }
        this.handleMessage(data);
      };
    });
  }

  private sendSetup(): void {
    // NOTE: Model is specified by the proxy, not the client
    // This is a security measure to prevent clients from using unauthorized models
    const setup: GeminiSetupMessage = {
      setup: {
        // model is intentionally omitted - proxy specifies it
        generationConfig: {
          responseModalities: ['AUDIO'],
          speechConfig: {
            voiceConfig: {
              prebuiltVoiceConfig: {
                voiceName: this.config.voiceName || 'Aoede',
              },
            },
          },
        },
        systemInstruction: {
          parts: [{ text: this.config.systemPrompt }],
        },
        realtimeInputConfig: {
          automaticActivityDetection: {
            disabled: false,
          },
        },
        inputAudioTranscription: {},
        outputAudioTranscription: {},
      },
    };

    this.ws?.send(JSON.stringify(setup));
  }

  private setupCompleteResolver: (() => void) | null = null;

  private waitForSetupComplete(): Promise<void> {
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error('Setup timeout'));
      }, 10000);

      this.setupCompleteResolver = () => {
        clearTimeout(timeout);
        resolve();
      };
    });
  }

  private handleMessage(data: string): void {
    let message: GeminiServerContent;
    try {
      message = JSON.parse(data);
      // Reset counter on successful parse
      this.consecutiveParseErrors = 0;
    } catch (error) {
      // Track consecutive parse failures
      this.consecutiveParseErrors++;
      const preview = data.length > 100 ? data.substring(0, 100) + '...' : data;
      console.error(
        '[VapSession] Failed to parse message:',
        error,
        `(${this.consecutiveParseErrors}/${this.MAX_CONSECUTIVE_PARSE_ERRORS})`,
        'Preview:',
        preview
      );

      // Notify user if parse errors are persistent - indicates protocol issue
      if (this.consecutiveParseErrors >= this.MAX_CONSECUTIVE_PARSE_ERRORS) {
        this.config.onError?.(
          new Error('Connection experiencing persistent protocol errors. Please try reconnecting.')
        );
        // Reset counter to avoid repeated notifications
        this.consecutiveParseErrors = 0;
      }
      return;
    }

    // Setup complete
    if (message.setupComplete) {
      this.setupCompleteResolver?.();
      this.setupCompleteResolver = null;
      return;
    }

    // Server content
    if (message.serverContent) {
      this.handleServerContent(message.serverContent);
    }
  }

  private handleServerContent(content: GeminiServerContent['serverContent']): void {
    if (!content) return;

    // Input (user) transcription
    if (content.inputTranscription?.text) {
      this.pendingUserText += content.inputTranscription.text;
    }

    // Output (agent) transcription
    if (content.outputTranscription?.text) {
      this.pendingAgentText += content.outputTranscription.text;
    }

    // Audio output
    if (content.modelTurn?.parts) {
      for (const part of content.modelTurn.parts) {
        if (part.inlineData?.data) {
          this.audioPlayback?.queueAudio(part.inlineData.data);
        }
      }
    }

    // Turn complete - flush transcript
    if (content.turnComplete || content.interrupted) {
      this.flushPendingTranscript();
    }
  }

  private flushPendingTranscript(): void {
    const timestamp = new Date().toISOString();

    // Get current phase for tagging (F-004)
    // Use 'in_character' as default if phase control is disabled
    const phase = this.currentPhase ?? 'in_character';

    // Flush user transcript
    if (this.pendingUserText.trim()) {
      const turn: VapTranscriptTurn = {
        role: 'user',
        text: this.pendingUserText.trim(),
        timestamp,
        phase,
      };
      this.transcript.push(turn);
      this.config.onTranscriptUpdate?.(turn);
      this.pendingUserText = '';
    }

    // Flush agent transcript
    if (this.pendingAgentText.trim()) {
      const turn: VapTranscriptTurn = {
        role: 'agent',
        text: this.pendingAgentText.trim(),
        timestamp,
        phase,
      };
      this.transcript.push(turn);
      this.config.onTranscriptUpdate?.(turn);
      this.pendingAgentText = '';
    }
  }

  // ==========================================================================
  // Private: Audio Management
  // ==========================================================================

  private async initializeAudio(): Promise<void> {
    // Initialize playback first (24kHz output from Gemini)
    this.audioPlayback = new AudioPlayback({
      sampleRate: 24000,
      channels: 1,
      bufferSize: 4096,
    });
    await this.audioPlayback.initialize();

    // Initialize capture (16kHz input to Gemini)
    const captureCallbacks: AudioCaptureCallbacks = {
      onAudioChunk: (pcmData: ArrayBuffer) => {
        this.sendAudioChunk(pcmData);
      },
      onVolumeLevel: () => {
        // Could emit volume level for UI if needed
      },
      onError: (error: Error) => {
        this.config.onError?.(error);
      },
    };

    this.audioCapture = new AudioCapture(
      {
        targetSampleRate: 16000,
        bufferDurationMs: 100,
        enableRecording: false, // VAP uses transcript, not audio recording
      },
      captureCallbacks
    );

    await this.audioCapture.initialize();
    this.audioCapture.start();
  }

  private stopAudio(): void {
    this.audioCapture?.stop();
    this.audioCapture = null;

    this.audioPlayback?.stop();
    this.audioPlayback = null;
  }

  private sendAudioChunk(pcmData: ArrayBuffer): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    if (this.status !== 'active') return;

    // Convert ArrayBuffer to base64
    const base64 = this.arrayBufferToBase64(pcmData);

    const message: GeminiRealtimeInput = {
      realtimeInput: {
        mediaChunks: [
          {
            mimeType: 'audio/pcm;rate=16000',
            data: base64,
          },
        ],
      },
    };

    this.ws.send(JSON.stringify(message));
  }

  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.length; i++) {
      binary += String.fromCharCode(bytes[i]!);
    }
    return btoa(binary);
  }

  // ==========================================================================
  // Private: Status Management
  // ==========================================================================

  private setStatus(status: VapSessionStatus): void {
    this.status = status;
    this.config.onStatusChange?.(status);
  }
}

// ============================================================================
// Factory Function
// ============================================================================

/**
 * Create a new VAP voice session
 */
export function createVapSession(config: VapSessionConfig): VapSession {
  return new VapSession(config);
}

